import React, { useState, useRef, useEffect } from 'react';
import { Camera, Save, User, Bell, Clock, Smartphone, Mail, RefreshCw, Link2Off, CheckCircle2, AlertTriangle, LogOut, Crown, Gift, Copy, Palette } from 'lucide-react';
import * as api from '../lib/api';
import { trackRecentPurchase } from '../lib/metaPixel';
import { downscaleImage } from '../lib/image';
import ProfilePhoto from '../components/ProfilePhoto';
import { toast } from 'sonner';
import { usePaywall } from '../context/PaywallContext';
import { useTheme } from '../context/ThemeContext';
import { Switch } from '../components/ui/switch';
import AccountDataCard from '../components/AccountDataCard';
import { redirectToGoogleConsent } from '../components/GoogleConsentNotice';
import { friendlyError } from '../lib/errorReporting';
import {
    isPushSupported, getPermission, enablePush, disablePush,
    getCurrentSubscription, resyncSubscription, localTimeToUtc, isIOS, isStandalone,
} from '../lib/push';

const THEME_OPTIONS = [
    { id: 'system', label: 'Automático' },
    { id: 'light', label: 'Claro' },
    { id: 'dark', label: 'Oscuro' },
];

function ThemeCard({ onProfileUpdate }) {
    const { preference, setPreference } = useTheme();
    const choose = async (id) => {
        if (id === preference) return;
        setPreference(id);
        try {
            onProfileUpdate(await api.updateProfile({ theme_preference: id }));
        } catch {
            // Still applied (and remembered) on this device; only the cross-device sync failed.
            toast.error('No se pudo guardar en tu cuenta, pero se aplicó en este dispositivo');
        }
    };
    return (
        <div className="glass-card rounded-2xl p-6" data-testid="theme-card">
            <h3 className="text-lg font-medium text-white mb-1 flex items-center gap-2">
                <Palette className="w-5 h-5 text-violet-400" />
                Apariencia
            </h3>
            <p className="text-sm text-slate-400 mb-4">Automático sigue el modo claro u oscuro de tu teléfono.</p>
            <div className="grid grid-cols-3 gap-3" role="radiogroup" aria-label="Tema">
                {THEME_OPTIONS.map(opt => (
                    <button
                        key={opt.id}
                        type="button"
                        role="radio"
                        aria-checked={preference === opt.id}
                        onClick={() => choose(opt.id)}
                        data-testid={`theme-${opt.id}`}
                        className={`min-h-[44px] py-2 px-3 rounded-xl border text-sm font-medium transition-all ${preference === opt.id
                            ? 'bg-violet-500/20 border-violet-500/50 text-violet-400'
                            : 'bg-slate-900/50 border-white/5 text-slate-400 hover:bg-slate-800'
                        }`}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

export default function Settings({ user, profile, onProfileUpdate, onLogout }) {
    const { openPaywall } = usePaywall();
    const [name, setName] = useState(profile?.name || user?.name || '');
    const [budgetPeriod, setBudgetPeriod] = useState(profile?.budget_period || 'monthly');
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef(null);

    // ----- Last crash (see components/ErrorBoundary.js) -----
    // Surfaces whatever ErrorBoundary or the window error/rejection
    // listeners in index.js last wrote to localStorage, so a report of
    // "the screen went blank" has somewhere to point to even if the user
    // didn't screenshot the error fallback in the moment.

    // ----- Notifications -----
    const pushSupported = isPushSupported();
    const iosNeedsInstall = isIOS() && !isStandalone();
    const [notifEnabled, setNotifEnabled] = useState(profile?.notifications_enabled || false);
    const [reminderTime, setReminderTime] = useState(profile?.reminder_time || '20:00');
    const [notifBusy, setNotifBusy] = useState(false);
    const [permission, setPermission] = useState(pushSupported ? getPermission() : 'denied');

    // Independent of push — resumen semanal / reactivación / rachas por correo
    // (see backend/email_lifecycle.py). Missing on an old cached profile is
    // treated as enabled, matching the Profile model's default=True.
    const [emailNotifEnabled, setEmailNotifEnabled] = useState(profile?.email_notifications_enabled !== false);
    const [emailNotifBusy, setEmailNotifBusy] = useState(false);

    // ----- Email integrations (Gmail) -----
    const [gmail, setGmail] = useState(null);          // integration doc or null
    const [gmailEnabled, setGmailEnabled] = useState(true);
    const [gmailBusy, setGmailBusy] = useState(false);
    // While the Google OAuth consent screen is still in Testing mode, real
    // (non-allow-listed) users can't complete the real connect flow — see
    // GOOGLE_OAUTH_VERIFIED's docstring in server.py. "Solicitar acceso"
    // captures interest instead until that flips to true.
    const [gmailOAuthVerified, setGmailOAuthVerified] = useState(true);
    const [gmailAccessRequestedAt, setGmailAccessRequestedAt] = useState(null);

    // ----- Billing (Pro plan) -----
    const [billing, setBilling] = useState(null);
    const [billingBusy, setBillingBusy] = useState(false);
    const [billingPlan, setBillingPlan] = useState('monthly');

    // ----- Referrals -----
    const [referral, setReferral] = useState(null);
    useEffect(() => {
        api.getReferralInfo().then(setReferral).catch(() => {});
    }, []);

    const loadBilling = async () => {
        try {
            const status = await api.getBillingStatus();
            setBilling(status);
            return status;
        } catch (_) { return null; /* best effort */ }
    };

    // Monthly Pro (Wompi recurring link): Wompi never redirects back, so when
    // a checkout or a not-yet-charged subscription is pending, ask the backend
    // to check Wompi right away and a few more times while the charge lands.
    const checkSubscription = async () => {
        try {
            await api.refreshSubscription();
        } catch (_) { /* best effort */ }
        return loadBilling();
    };

    useEffect(() => {
        loadBilling().then(status => {
            if (!['pending', 'awaiting_payment'].includes(status?.subscription?.state)) return;
            const attempts = [0, 20000, 60000];
            const run = async (i) => {
                const st = await checkSubscription();
                if (st?.subscription?.state === 'active') return toast.success('¡Listo! Tu suscripción a MiPisto Pro está activa');
                if (i + 1 < attempts.length) setTimeout(() => run(i + 1), attempts[i + 1] - attempts[i]);
            };
            run(0);
        });
        const params = new URLSearchParams(window.location.search);
        if (params.get('billing') === 'success') {
            toast.success('¡Pago recibido! Activando tu plan Pro…');
            window.history.replaceState({}, '', window.location.pathname);
            // Wompi redirects back here even before the webhook arrives (or
            // if the payment failed), so this URL alone proves nothing. Meta
            // Purchase only fires when the backend already has the payment
            // as 'paid', retrying a few times while the webhook arrives.
            // The source of truth is the CAPI event the backend sends from
            // _confirm_payment; this is the browser half, same event_id.
            const attempts = [2500, 6000, 12000];
            const check = async (i) => {
                const status = await loadBilling();
                if (trackRecentPurchase(status?.last_purchase)) return;
                if (i + 1 < attempts.length) setTimeout(() => check(i + 1), attempts[i + 1] - attempts[i]);
            };
            setTimeout(() => check(0), attempts[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleUpgrade = async (plan = billingPlan) => {
        setBillingBusy(true);
        try {
            const { url } = await api.createCheckout(plan);
            window.location.href = url;
        } catch (e) {
            setBillingBusy(false);
            toast.error(friendlyError(e, 'No se pudo iniciar el pago'));
        }
    };

    const loadIntegrations = async () => {
        try {
            const data = await api.getIntegrations();
            setGmailEnabled(data.google_oauth_enabled);
            setGmailOAuthVerified(data.google_oauth_verified);
            setGmailAccessRequestedAt(data.gmail_access_requested_at);
            setGmail((data.integrations || []).find(i => i.provider === 'gmail') || null);
        } catch (_) { /* best effort */ }
    };

    useEffect(() => {
        loadIntegrations();
        const params = new URLSearchParams(window.location.search);
        const g = params.get('gmail');
        if (g === 'connected') {
            toast.success('Gmail conectado. Sincronizando tus correos…');
            api.syncGoogle().then(loadIntegrations).catch(() => {});
        } else if (g === 'error') {
            toast.error('No se pudo conectar Gmail. Inténtalo de nuevo.');
        } else if (g === 'auto_connect') {
            // Landed here from the "your Gmail access was approved" email or
            // GmailAccessGrantedModal — jump straight into the OAuth redirect
            // instead of making them find and click "Conectar Gmail" again.
            api.ackGmailAccessGranted().catch(() => {});
            handleConnectGoogle();
        }
        if (g) window.history.replaceState({}, '', window.location.pathname);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleConnectGoogle = async () => {
        setGmailBusy(true);
        try {
            const went = await redirectToGoogleConsent(await api.connectGoogle());
            if (!went) setGmailBusy(false);
        } catch (e) {
            setGmailBusy(false);
            if (api.isPaywallError(e)) {
                openPaywall(e.response.data.detail);
            } else {
                toast.error(friendlyError(e, 'No se pudo iniciar la conexión con Google'));
            }
        }
    };

    const handleSyncGoogle = async () => {
        setGmailBusy(true);
        try {
            const res = await api.syncGoogle();
            await loadIntegrations();
            toast.success(res.new_detections > 0
                ? `${res.new_detections} transacción(es) nueva(s) detectada(s)`
                : 'Sincronizado. Sin transacciones nuevas.');
        } catch (e) {
            if (api.isPaywallError(e)) {
                openPaywall(e.response.data.detail);
            } else {
                toast.error(friendlyError(e, 'Error al sincronizar'));
            }
        } finally {
            setGmailBusy(false);
        }
    };

    const handleRequestGmailAccess = async () => {
        setGmailBusy(true);
        try {
            const res = await api.requestGmailAccess();
            setGmailAccessRequestedAt(res.requested_at);
            toast.success('¡Listo! Te avisaremos en cuanto esté disponible.');
        } catch (e) {
            toast.error(friendlyError(e, 'No se pudo enviar la solicitud'));
        } finally {
            setGmailBusy(false);
        }
    };

    const handleDisconnectGoogle = async () => {
        setGmailBusy(true);
        try {
            await api.disconnectGoogle();
            setGmail(null);
            toast.success('Gmail desconectado');
        } catch (e) {
            toast.error('Error al desconectar');
        } finally {
            setGmailBusy(false);
        }
    };

    // If the backend thinks notifications are on but this device has no active
    // subscription (e.g. a new device, or the user revoked permission), reflect OFF.
    useEffect(() => {
        if (!pushSupported || !profile?.notifications_enabled) return;
        (async () => {
            const sub = await getCurrentSubscription();
            if (!sub || getPermission() !== 'granted') setNotifEnabled(false);
            else resyncSubscription();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleToggleNotifications = async (checked) => {
        setNotifBusy(true);
        if (checked) {
            try {
                await enablePush();
                const updated = await api.updateProfile({
                    notifications_enabled: true,
                    reminder_time: reminderTime,
                    reminder_time_utc: localTimeToUtc(reminderTime),
                    tz_offset: new Date().getTimezoneOffset(),
                });
                onProfileUpdate(updated);
                setNotifEnabled(true);
                setPermission(getPermission());
                toast.success('Notificaciones activadas');
                try { await api.sendTestPush(); } catch (_) { /* best effort */ }
            } catch (e) {
                setNotifEnabled(false);
                setPermission(getPermission());
                const code = e?.code;
                if (code === 'permission-denied') toast.error('Permiso de notificaciones denegado');
                else if (code === 'unsupported') toast.error('Tu navegador no soporta notificaciones push');
                else if (code === 'server-not-configured') toast.error('Las notificaciones no están configuradas en el servidor');
                else {
                    console.error('enablePush failed:', e);
                    toast.error('No se pudieron activar las notificaciones. Intenta de nuevo.');
                }
            }
        } else {
            try {
                await disablePush();
                const updated = await api.updateProfile({ notifications_enabled: false });
                onProfileUpdate(updated);
                setNotifEnabled(false);
                toast.success('Notificaciones desactivadas');
            } catch (e) {
                toast.error('Error al desactivar las notificaciones');
            }
        }
        setNotifBusy(false);
    };

    const handleReminderTimeChange = async (val) => {
        setReminderTime(val);
        if (!notifEnabled || !val) return;
        try {
            const updated = await api.updateProfile({
                reminder_time: val,
                reminder_time_utc: localTimeToUtc(val),
                tz_offset: new Date().getTimezoneOffset(),
            });
            onProfileUpdate(updated);
            toast.success('Hora de recordatorio actualizada');
        } catch (e) {
            toast.error('Error al guardar la hora');
        }
    };

    const handleToggleEmailNotifications = async (checked) => {
        setEmailNotifBusy(true);
        try {
            const updated = await api.updateProfile({ email_notifications_enabled: checked });
            onProfileUpdate(updated);
            setEmailNotifEnabled(checked);
            toast.success(checked ? 'Correos activados' : 'Correos desactivados');
        } catch (e) {
            toast.error('Error al guardar la preferencia');
        } finally {
            setEmailNotifBusy(false);
        }
    };


    const handleSave = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const updatedProfile = await api.updateProfile({
                name,
                budget_period: budgetPeriod
            });
            onProfileUpdate(updatedProfile);
            toast.success('Perfil actualizado correctamente');
        } catch (e) {
            console.error(e);
            toast.error('Error al actualizar el perfil');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Por favor selecciona una imagen válida');
            return;
        }

        try {
            toast.loading('Subiendo foto...', { id: 'photo-upload' });
            // ~512px is plenty for an avatar and keeps the stored photo small.
            const updatedProfile = await api.uploadProfilePhoto(await downscaleImage(file, 512, 'perfil.jpg'));
            onProfileUpdate(updatedProfile);
            toast.success('Foto actualizada con éxito', { id: 'photo-upload' });
        } catch (err) {
            console.error(err);
            toast.error('Error al subir la foto', { id: 'photo-upload' });
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="sticky top-[env(safe-area-inset-top)] z-10 bg-surface pb-4 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Poppins' }}>Configuración</h1>
                    <p className="text-slate-400">Gestiona tu perfil y preferencias</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-1 space-y-6">
                    <div className="glass-card rounded-2xl p-6 flex flex-col items-center text-center">
                        <div className="relative group mb-4">
                            <div className="w-24 h-24 rounded-full overflow-hidden bg-slate-800 border-2 border-violet-500/20 flex items-center justify-center">
                                <ProfilePhoto src={profile?.photo} alt="Perfil" fallback={<User className="w-12 h-12 text-slate-400" />} />
                            </div>
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-full cursor-pointer"
                            >
                                <Camera className="w-6 h-6 text-white" />
                            </button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handlePhotoUpload}
                                className="hidden"
                                accept="image/*"
                            />
                        </div>
                        <h3 className="text-lg font-medium text-white">{profile?.name || user?.name}</h3>
                        <p className="text-sm text-slate-400">{user?.email}</p>
                    </div>

                    {onLogout && (
                        <button
                            onClick={onLogout}
                            data-testid="settings-logout"
                            className="w-full flex items-center justify-center gap-2 glass-card rounded-2xl py-3 text-sm font-medium text-slate-400 hover:text-rose-400 transition-colors"
                        >
                            <LogOut className="w-4 h-4" /> Cerrar sesión
                        </button>
                    )}
                </div>

                <div className="md:col-span-2 space-y-6">
                    <form onSubmit={handleSave} className="glass-card rounded-2xl p-6">
                        <h3 className="text-lg font-medium text-white mb-6 flex items-center gap-2">
                            <User className="w-5 h-5 text-violet-400" />
                            Detalles del Perfil
                        </h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1.5">Nombre completo</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all font-medium"
                                    placeholder="Tu nombre"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1.5">Periodo de Presupuesto</label>
                                <div className="grid grid-cols-3 gap-3">
                                    {[
                                        { id: 'daily', label: 'Diario' },
                                        { id: 'weekly', label: 'Semanal' },
                                        { id: 'monthly', label: 'Mensual' },
                                    ].map(period => (
                                        <button
                                            key={period.id}
                                            type="button"
                                            onClick={() => setBudgetPeriod(period.id)}
                                            className={`py-2 px-3 rounded-xl border text-sm font-medium transition-all ${budgetPeriod === period.id
                                                    ? 'bg-violet-500/20 border-violet-500/50 text-violet-400'
                                                    : 'bg-slate-900/50 border-white/5 text-slate-400 hover:bg-slate-800'
                                                }`}
                                        >
                                            {period.label}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-slate-500 mt-2">
                                    Esto define cómo se calculan y se muestran tus reportes financieros y alertas de presupuestos por defecto.
                                </p>
                            </div>

                            <div className="pt-4 flex justify-end">
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="flex items-center gap-2 bg-violet-500 hover:bg-violet-600 text-on-brand px-5 py-2.5 rounded-xl font-medium transition-colors shadow-lg shadow-violet-500/20 disabled:opacity-50"
                                >
                                    {isLoading ? (
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4" />
                                    )}
                                    Guardar Cambios
                                </button>
                            </div>
                        </div>
                    </form>

                    {/* Apariencia — applies instantly on this device; saved to the
                        profile so it follows the account to other devices. */}
                    <ThemeCard onProfileUpdate={onProfileUpdate} />

                    {/* Plan / billing */}
                    <div className="glass-card rounded-2xl p-6" data-testid="billing-card">
                        <h3 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
                            <Crown className="w-5 h-5 text-violet-400" />
                            Tu plan
                        </h3>

                        {!billing ? (
                            <div className="h-12" />
                        ) : billing.plan === 'pro' && billing.subscription?.state === 'active' ? (
                            <div>
                                <p className="text-sm font-medium text-emerald-400">MiPisto Pro activo</p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    Suscripción mensual: se cobra automáticamente el día {billing.subscription.charge_day} de cada mes.
                                </p>
                            </div>
                        ) : ['pending', 'awaiting_payment'].includes(billing.subscription?.state) && billing.plan !== 'pro' ? (
                            <div className="space-y-3" data-testid="billing-subscription-pending">
                                <p className="text-sm text-slate-300">
                                    {billing.subscription.state === 'pending'
                                        ? 'Estamos esperando la confirmación de tu suscripción en Wompi. Si ya la completaste, puede tardar unos minutos.'
                                        : 'Tu suscripción está registrada, pero Wompi todavía no confirma el cobro. Si tu tarjeta fue rechazada, intenta con otra.'}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        onClick={async () => {
                                            setBillingBusy(true);
                                            const st = await checkSubscription();
                                            setBillingBusy(false);
                                            if (st?.subscription?.state === 'active') toast.success('¡Listo! Tu suscripción a MiPisto Pro está activa');
                                            else toast.message('Todavía no vemos el cobro. Vuelve a revisar en unos minutos.');
                                        }}
                                        disabled={billingBusy}
                                        className="bg-violet-500 hover:bg-violet-600 text-on-brand px-4 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                                    >
                                        Ya pagué, revisar
                                    </button>
                                    <button
                                        onClick={() => handleUpgrade('monthly')}
                                        disabled={billingBusy || !billing.billing_enabled}
                                        className="bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                                    >
                                        Volver a Wompi
                                    </button>
                                </div>
                            </div>
                        ) : billing.plan === 'pro' ? (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between gap-4 flex-wrap">
                                    <div>
                                        <p className="text-sm font-medium text-emerald-400">MiPisto Pro activo</p>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            {billing.plan_expires_at
                                                ? `Renueva el ${new Date(billing.plan_expires_at).toLocaleDateString()}`
                                                : ''}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => handleUpgrade()}
                                        disabled={billingBusy || !billing.billing_enabled}
                                        data-testid="billing-renew-btn"
                                        className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                                    >
                                        Renovar ahora
                                    </button>
                                </div>
                                <BillingPlanToggle billing={billing} value={billingPlan} onChange={setBillingPlan} />
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <p className="text-sm text-slate-300">
                                    {billing.in_trial ? (
                                        <>Estás en tu prueba gratuita de Pro — te quedan{' '}
                                            <span className="text-violet-400 font-semibold">{billing.days_left} día(s)</span>.{' '}</>
                                    ) : (
                                        <>Tu prueba gratuita terminó. </>
                                    )}
                                    Incluye detección automática por Gmail, registro por voz y tu Coach financiero.
                                </p>
                                <BillingPlanToggle billing={billing} value={billingPlan} onChange={setBillingPlan} />
                                <button
                                    onClick={() => handleUpgrade()}
                                    disabled={billingBusy || !billing.billing_enabled}
                                    data-testid="billing-upgrade-btn"
                                    className="flex items-center gap-2 bg-violet-500 hover:bg-violet-600 text-on-brand px-5 py-2.5 rounded-xl font-medium transition-colors shadow-lg shadow-violet-500/20 disabled:opacity-50"
                                >
                                    {billingPlan === 'annual'
                                        ? `Actualizar a Pro — $${billing.price_annual}/año`
                                        : `Actualizar a Pro — $${billing.price}/mes`}
                                </button>
                                {billingPlan === 'monthly' && billing.monthly_charge_day && (
                                    <p className="text-xs text-slate-500">
                                        Se cobra hoy y después el día {billing.monthly_charge_day} de cada mes, automáticamente.
                                    </p>
                                )}
                                {!billing.billing_enabled && (
                                    <p className="text-xs text-slate-500">Los pagos aún no están configurados en el servidor.</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Referrals */}
                    {referral && (
                        <div className="glass-card rounded-2xl p-6" data-testid="referral-card">
                            <h3 className="text-lg font-medium text-white mb-1 flex items-center gap-2">
                                <Gift className="w-5 h-5 text-violet-400" />
                                Invitá a un amigo
                            </h3>
                            <p className="text-sm text-slate-300 mb-4">
                                Vos y tu amigo ganan <span className="text-violet-400 font-semibold">{referral.reward_days} días de Pro gratis</span> cuando
                                registre su primer gasto o ingreso.
                                {referral.referred_count > 0 && (
                                    <> Ya invitaste a <span className="text-white font-medium">{referral.referred_count}</span> persona(s).</>
                                )}
                            </p>
                            <div className="flex gap-2">
                                <input
                                    readOnly
                                    value={referral.link}
                                    onClick={(e) => e.target.select()}
                                    className="bg-slate-900/50 border border-white/10 rounded-xl px-4 py-2 text-sm text-slate-300 flex-1 min-w-0 focus:outline-none"
                                />
                                <button
                                    onClick={() => {
                                        navigator.clipboard?.writeText(referral.link);
                                        toast.success('Link copiado');
                                    }}
                                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors shrink-0"
                                >
                                    <Copy className="w-4 h-4" /> Copiar
                                </button>
                            </div>
                            <a
                                href={`https://wa.me/?text=${encodeURIComponent(
                                    `Che, te recomiendo MiPisto para llevar tus finanzas — con este link ambos ganamos ${referral.reward_days} días de Pro gratis: ${referral.link}`
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-3 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-on-brand px-4 py-2 rounded-xl text-sm font-medium transition-colors"
                            >
                                Compartir por WhatsApp
                            </a>
                        </div>
                    )}

                    {/* Notifications */}
                    <div className="glass-card rounded-2xl p-6" data-testid="notifications-card">
                        <h3 className="text-lg font-medium text-white mb-1 flex items-center gap-2">
                            <Bell className="w-5 h-5 text-violet-400" />
                            Notificaciones
                        </h3>
                        <p className="text-sm text-slate-400 mb-6">
                            Alertas de presupuesto, recordatorios y tu resumen semanal en tu dispositivo, como una app nativa.
                        </p>

                        {!pushSupported ? (
                            <div className="rounded-xl bg-slate-900/50 border border-white/10 p-4 text-sm text-slate-400">
                                Tu navegador no soporta notificaciones push.
                            </div>
                        ) : iosNeedsInstall ? (
                            <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 text-sm text-amber-300 flex items-start gap-3">
                                <Smartphone className="w-5 h-5 shrink-0 mt-0.5" />
                                <span>
                                    En iPhone/iPad, primero añade MiPisto a tu pantalla de inicio
                                    (Compartir → “Añadir a inicio”). Luego abre la app desde el ícono para
                                    activar las notificaciones.
                                </span>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <p className="text-sm font-medium text-white">Notificaciones push</p>
                                        <p className="text-xs text-slate-500">
                                            Avisos de presupuesto, recordatorios diarios, rachas y resumen semanal.
                                        </p>
                                    </div>
                                    <Switch
                                        checked={notifEnabled}
                                        disabled={notifBusy}
                                        onCheckedChange={handleToggleNotifications}
                                        className="data-[state=checked]:bg-violet-500"
                                        data-testid="notifications-toggle"
                                    />
                                </div>

                                {permission === 'denied' && !notifEnabled && (
                                    <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 p-3 text-xs text-rose-300">
                                        Has bloqueado las notificaciones. Habilítalas en los ajustes de tu
                                        navegador para este sitio y vuelve a intentarlo.
                                    </div>
                                )}

                                {notifEnabled && (
                                    <>
                                        <div className="border-t border-white/5 pt-4">
                                            <label className="block text-sm font-medium text-slate-300 mb-1.5 flex items-center gap-2">
                                                <Clock className="w-4 h-4 text-violet-400" />
                                                Recordatorio diario
                                            </label>
                                            <p className="text-xs text-slate-500 mb-2">
                                                La hora para tu recordatorio diario y el resumen de los domingos. (Máx. 2 notificaciones al día.)
                                            </p>
                                            <input
                                                type="time"
                                                value={reminderTime}
                                                onChange={(e) => handleReminderTimeChange(e.target.value)}
                                                className="bg-slate-900/50 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all font-medium"
                                                data-testid="reminder-time-input"
                                            />
                                        </div>

                                        <div className="border-t border-white/5 pt-4">
                                            <p className="text-xs font-medium text-slate-300 mb-2">Qué recibirás</p>
                                            <ul className="space-y-1.5 text-xs text-slate-400">
                                                <li className="flex items-start gap-2"><span>🚨</span> Avisos al acercarte o superar un presupuesto</li>
                                                <li className="flex items-start gap-2"><span>👋</span> Un recordatorio diario (solo si no registraste nada)</li>
                                                <li className="flex items-start gap-2"><span>🔥</span> Celebración de tus rachas de registro</li>
                                                <li className="flex items-start gap-2"><span>📊</span> Un resumen de tu semana los domingos</li>
                                            </ul>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Email lifecycle (weekly report / reactivation / rachas) — independent of push above */}
                    <div className="glass-card rounded-2xl p-6" data-testid="email-notifications-card">
                        <h3 className="text-lg font-medium text-white mb-1 flex items-center gap-2">
                            <Mail className="w-5 h-5 text-violet-400" />
                            Notificaciones por correo
                        </h3>
                        <p className="text-sm text-slate-400 mb-6">
                            Tu resumen semanal, un recordatorio si dejas de registrar por un tiempo, y la
                            celebración de tus rachas — directo a tu correo, sin depender de las notificaciones push.
                        </p>
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <p className="text-sm font-medium text-white">Correos de MiPisto</p>
                                <p className="text-xs text-slate-500">Podés darte de baja también desde el enlace en cualquiera de estos correos.</p>
                            </div>
                            <Switch
                                checked={emailNotifEnabled}
                                disabled={emailNotifBusy}
                                onCheckedChange={handleToggleEmailNotifications}
                                className="data-[state=checked]:bg-violet-500"
                                data-testid="email-notifications-toggle"
                            />
                        </div>
                    </div>

                    {/* Connected accounts (email integrations) */}
                    <div className="glass-card rounded-2xl p-6" data-testid="integrations-card">
                        <h3 className="text-lg font-medium text-white mb-1 flex items-center gap-2">
                            <Mail className="w-5 h-5 text-violet-400" />
                            Cuentas conectadas
                        </h3>
                        <p className="text-sm text-slate-400 mb-6">
                            Conecta tu correo para detectar automáticamente compras, pagos y transferencias
                            de tu banco o billetera. Las transacciones detectadas se agregan directo a tu
                            historial (marcadas con <Mail className="w-3 h-3 inline align-baseline" />)
                            — puedes editarlas o eliminarlas si algo salió mal.
                        </p>

                        {!gmailEnabled ? (
                            <div className="rounded-xl bg-slate-900/50 border border-white/10 p-4 text-sm text-slate-400">
                                La conexión con Gmail no está configurada en el servidor.
                            </div>
                        ) : !gmail && !gmailOAuthVerified ? (
                            gmailAccessRequestedAt ? (
                                <div className="flex items-center gap-2 text-sm text-emerald-300">
                                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                                    Ya solicitaste acceso — te avisaremos cuando esté disponible.
                                </div>
                            ) : (
                                <>
                                    <button
                                        onClick={handleRequestGmailAccess}
                                        disabled={gmailBusy}
                                        className="flex items-center gap-2 bg-[#ffffff] hover:bg-[#f1f3f4] text-[#1f1f1f] px-5 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50"
                                    >
                                        <Mail className="w-4 h-4 text-[#EA4335]" />
                                        Solicitar acceso a Gmail
                                    </button>
                                    <p className="text-xs text-slate-500 mt-2">
                                        Estamos terminando la verificación con Google — solicita acceso y te
                                        avisamos apenas esté lista.
                                    </p>
                                </>
                            )
                        ) : !gmail ? (
                            <button
                                onClick={handleConnectGoogle}
                                disabled={gmailBusy}
                                className="flex items-center gap-2 bg-[#ffffff] hover:bg-[#f1f3f4] text-[#1f1f1f] px-5 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50"
                            >
                                <Mail className="w-4 h-4 text-[#EA4335]" />
                                Conectar Gmail
                            </button>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between gap-4 flex-wrap">
                                    <div className="flex items-center gap-3">
                                        {gmail.status === 'reauth_required' ? (
                                            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
                                        ) : (
                                            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                                        )}
                                        <div>
                                            <p className="text-sm font-medium text-white">{gmail.email_address || 'Gmail'}</p>
                                            <p className="text-xs text-slate-500">
                                                {gmail.status === 'reauth_required'
                                                    ? 'Reconexión necesaria — vuelve a conectar.'
                                                    : gmail.last_sync_at
                                                        ? `Última sincronización: ${new Date(gmail.last_sync_at).toLocaleString()}`
                                                        : 'Aún no sincronizado.'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {gmail.status === 'reauth_required' ? (
                                            <button
                                                onClick={handleConnectGoogle}
                                                disabled={gmailBusy}
                                                className="flex items-center gap-2 bg-violet-500 hover:bg-violet-600 text-on-brand px-4 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                                            >
                                                <Mail className="w-4 h-4" /> Reconectar
                                            </button>
                                        ) : (
                                            <button
                                                onClick={handleSyncGoogle}
                                                disabled={gmailBusy}
                                                className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                                            >
                                                <RefreshCw className={`w-4 h-4 ${gmailBusy ? 'animate-spin' : ''}`} /> Sincronizar ahora
                                            </button>
                                        )}
                                        <button
                                            onClick={handleDisconnectGoogle}
                                            disabled={gmailBusy}
                                            className="flex items-center gap-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 px-4 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                                        >
                                            <Link2Off className="w-4 h-4" /> Desconectar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <AccountDataCard onDeleted={() => onLogout && onLogout()} />
                </div>
            </div>
        </div>
    );
}

// Monthly/annual pill selector shared by both billing-card branches (new
// purchase and renewal) — annual added 2026-09-13 to cut manual-renewal
// churn (12 chances a year to forget vs 1).
function BillingPlanToggle({ billing, value, onChange }) {
    const monthlyTotal = billing.price * 12;
    const savingsPct = Math.round((1 - billing.price_annual / monthlyTotal) * 100);
    return (
        <div className="flex gap-2">
            <button
                type="button"
                onClick={() => onChange('monthly')}
                className={`flex-1 rounded-xl border px-3 py-2 text-left transition-colors ${
                    value === 'monthly' ? 'border-violet-500 bg-violet-500/10' : 'border-white/10 hover:border-white/20'
                }`}
            >
                <p className="text-sm font-medium text-white">Mensual</p>
                <p className="text-xs text-slate-500">${billing.price}/mes</p>
            </button>
            <button
                type="button"
                onClick={() => onChange('annual')}
                className={`flex-1 rounded-xl border px-3 py-2 text-left transition-colors ${
                    value === 'annual' ? 'border-violet-500 bg-violet-500/10' : 'border-white/10 hover:border-white/20'
                }`}
            >
                <p className="text-sm font-medium text-white">
                    Anual <span className="text-emerald-400 font-normal">ahorra {savingsPct}%</span>
                </p>
                <p className="text-xs text-slate-500">${billing.price_annual}/año</p>
            </button>
        </div>
    );
}
